import { useEffect, useRef, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { doc, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { db, functions } from "../firebase/config";
import { httpsCallable } from "firebase/functions";
import { chunkFormData, llmManagementService, Prompt, SourceMaterial } from "../services/LLMManagementService";
import { PencilIcon, ArrowPathIcon, XMarkIcon, CheckIcon, DocumentTextIcon, ArrowUpTrayIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";

type TabType = 'source-material' | 'prompts';

const LLMManagementPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('prompts');

    // Source Material State
    const [sourceMaterial, setMaterial] = useState<SourceMaterial[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [editingChunk, setEditingChunk] = useState<SourceMaterial | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [updatingChunks, setUpdatingChunks] = useState<Set<string>>(new Set());
    const [showCSVUpload, setShowCSVUpload] = useState(false);
    const [csvUploading, setCSVUploading] = useState(false);
    const [csvUploadResult, setCSVUploadResult] = useState<{
        success: boolean;
        message: string;
        results?: {
            total: number;
            successful: number;
            failed: number;
            successfulChunks: string[];
            failedChunks: { chunk_id: string; error: string }[];
        };
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState<chunkFormData>({
        id: '',
        content: '',
        sourceDocument: '',
        topicTags: [],
        dataTypeTags: [],
        topicTagsInput: '',
        dataTypeTagsInput: '',
    });

    // Prompts State
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [promptsLoading, setPromptsLoading] = useState(true);
    const [promptSearchTerm, setPromptSearchTerm] = useState('');
    const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
    const [showPromptForm, setShowPromptForm] = useState(false);
    const [promptFormData, setPromptFormData] = useState<{
        name: string;
        template: string;
        description: string;
        variablesInput: string;
    }>({
        name: '',
        template: '',
        description: '',
        variablesInput: '',
    });
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);

    // Natural sort function for chunk IDs
    const naturalSort = (a: string, b: string): number => {
        const regex = /(\d+)|(\D+)/g;
        const aParts = a.match(regex) || [];
        const bParts = b.match(regex) || [];

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            if (!aParts[i]) return -1;
            if (!bParts[i]) return 1;

            const aIsNum = /^\d+$/.test(aParts[i]);
            const bIsNum = /^\d+$/.test(bParts[i]);

            if (aIsNum && bIsNum) {
                const diff = parseInt(aParts[i]) - parseInt(bParts[i]);
                if (diff !== 0) return diff;
            } else {
                const diff = aParts[i].localeCompare(bParts[i]);
                if (diff !== 0) return diff;
            }
        }

        return 0;
    };

    const fetchChunks = async () => {
        try {
            setLoading(true);
            setError(null);

            const materialData: SourceMaterial[] = await llmManagementService.getSourceMaterialChunks();
            materialData.sort((a, b) => a.id.localeCompare(b.id));

            setMaterial(materialData);
            setError(null);
        } catch (error: any) {
            console.error('Error fetching source material:', error);
            setError(`Failed to fetch source material: ${error.message}`);
            setMaterial([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPrompts = async () => {
        try {
            setPromptsLoading(true);
            const promptsData = await llmManagementService.getLLMPrompts();
            setPrompts(promptsData);
        } catch (error: any) {
            console.error('Error fetching prompts:', error);
            setError(`Failed to fetch prompts: ${error.message}`);
        } finally {
            setPromptsLoading(false);
        }
    };

    useEffect(() => {
        fetchChunks();
        fetchPrompts();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const topicTags = formData.topicTagsInput
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);

            const dataTypeTags = formData.dataTypeTagsInput
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);

            const chunkData = {
                id: formData.id,
                content: formData.content,
                sourceDocument: formData.sourceDocument,
                topicTags,
                dataTypeTags,
                version: editingChunk ? (editingChunk.version + 1) : 1,
                created: editingChunk ? editingChunk.created : Timestamp.now(),
                lastUpdated: Timestamp.now(),
                status: 'pending',
            };

            if (editingChunk) {
                await updateDoc(doc(db, `sourceMaterial/${formData.id}`), chunkData);
            } else {
                await setDoc(doc(db, `sourceMaterial/${formData.id}`), chunkData);
            }

            setFormData({
                id: '',
                content: '',
                sourceDocument: '',
                topicTags: [],
                dataTypeTags: [],
                topicTagsInput: '',
                dataTypeTagsInput: '',
            });
            setShowForm(false);
            setEditingChunk(null);
            await fetchChunks();
        } catch (error) {
            console.error('Error saving chunk:', error);
        }
    };

    const handlePromptSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const variables = promptFormData.variablesInput
                .split(',')
                .map(v => v.trim())
                .filter(v => v.length > 0);

            if (editingPrompt) {
                await llmManagementService.updatePrompt(editingPrompt.id, {
                    name: promptFormData.name,
                    template: promptFormData.template,
                    description: promptFormData.description,
                    variables,
                });
            } else {
                await llmManagementService.createPrompt({
                    name: promptFormData.name,
                    template: promptFormData.template,
                    description: promptFormData.description,
                    variables,
                });
            }

            setPromptFormData({
                name: '',
                template: '',
                description: '',
                variablesInput: '',
            });
            setShowPromptForm(false);
            setEditingPrompt(null);
            setShowTemplatePreview(false);
            await fetchPrompts();
        } catch (error) {
            console.error('Error saving prompt:', error);
        }
    };

    const handleUpdateIndex = async (chunkId: string) => {
        try {
            setUpdatingChunks(prev => new Set(prev).add(chunkId));

            const updateChunkIndex = httpsCallable(functions, 'updateChunkIndex');
            const result = await updateChunkIndex({ chunkId });

            console.log(`Index update result for ${chunkId}:`, result);
            await fetchChunks();
            console.log(`Successfully updated index for chunk: ${chunkId}`);
        } catch (error: any) {
            console.error(`Error updating index for chunk ${chunkId}:`, error);
            setError(`Failed to update index for chunk ${chunkId}: ${error.message}`);
        } finally {
            setUpdatingChunks(prev => {
                const newSet = new Set(prev);
                newSet.delete(chunkId);
                return newSet;
            });
        }
    };

    const cancelEdit = () => {
        setShowForm(false);
        setEditingChunk(null);
        setFormData({
            id: '',
            content: '',
            sourceDocument: '',
            topicTags: [],
            dataTypeTags: [],
            topicTagsInput: '',
            dataTypeTagsInput: '',
        });
    };

    const cancelPromptEdit = () => {
        setShowPromptForm(false);
        setEditingPrompt(null);
        setShowTemplatePreview(false);
        setPromptFormData({
            name: '',
            template: '',
            description: '',
            variablesInput: '',
        });
    };

    const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        setCSVUploading(true);
        setCSVUploadResult(null);
        setError(null);

        try {
            const csvContent = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target?.result as string);
                reader.onerror = reject;
                reader.readAsText(file);
            });

            const uploadSourceMaterialCSV = httpsCallable(functions, 'uploadSourceMaterialCSV');
            const result = await uploadSourceMaterialCSV({
                csvContent: csvContent,
                sourceDocument: file.name
            });

            const data = result.data as any;
            setCSVUploadResult(data);

            if (data.success) {
                await fetchChunks();
            }
        } catch (error: any) {
            console.error('Error uploading CSV:', error);
            setError(`Failed to upload CSV: ${error.message}`);
            setCSVUploadResult({
                success: false,
                message: `Upload failed: ${error.message}`
            });
        } finally {
            setCSVUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const filteredChunks = sourceMaterial.filter(chunk => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            chunk.id.toLowerCase().includes(searchLower) ||
            chunk.content.toLowerCase().includes(searchLower) ||
            chunk.sourceDocument?.toLowerCase().includes(searchLower) ||
            chunk.topicTags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
            chunk.dataTypeTags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
    }).sort((a, b) => naturalSort(a.id, b.id));

    const filteredPrompts = prompts.filter(prompt => {
        if (!promptSearchTerm) return true;
        const searchLower = promptSearchTerm.toLowerCase();
        return (
            prompt.name.toLowerCase().includes(searchLower) ||
            prompt.description?.toLowerCase().includes(searchLower) ||
            prompt.template.toLowerCase().includes(searchLower)
        );
    });

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 p-4 md:p-6 md:h-[calc(100vh-64px)] overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
                    <h1 className="text-xl md:text-2xl font-heading">The Wizard</h1>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 shrink-0">
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b shrink-0">
                    <button
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'prompts'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setActiveTab('prompts')}
                    >
                        LLM Prompts
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'source-material'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setActiveTab('source-material')}
                    >
                        Source Material
                    </button>
                </div>

                {/* Source Material Tab */}
                {activeTab === 'source-material' && (
                    <>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 shrink-0">
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn btn-ghost btn-sm !h-9 md:!h-10 text-xs md:text-sm"
                                    onClick={() => setShowCSVUpload(true)}
                                >
                                    <ArrowUpTrayIcon className="w-4 h-4" />
                                    <span className="hidden sm:inline">Upload CSV</span>
                                    <span className="sm:hidden">CSV</span>
                                </button>
                                <button className="btn btn-primary btn-sm !h-9 md:!h-10 text-xs md:text-sm"
                                    onClick={() => setShowForm(true)}
                                >
                                    Add Chunk
                                </button>
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div className="w-full grid grid-cols-3 gap-2 shrink-0">
                            <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                                <div className="text-xl md:text-2xl text-primary font-bold">
                                    {sourceMaterial.length}
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Chunks</div>
                            </div>
                            <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                                <div className="text-xl md:text-2xl text-blue-700 font-bold">
                                    {sourceMaterial.filter(c => c.vectorIndexed).length}
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase">Indexed</div>
                            </div>
                            <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                                <div className="text-xl md:text-2xl text-orange-600 font-bold">
                                    {sourceMaterial.filter(c => !c.vectorIndexed).length}
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase">Not Indexed</div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center gap-6 relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search materials..."
                                    className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Chunks Table */}
                        <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                            <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_4fr_2fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                                <div className="hidden md:block">Chunk ID</div>
                                <div className="hidden md:block">Status</div>
                                <div>Content</div>
                                <div className="hidden md:block">Tags</div>
                                <div>Indexed</div>
                                <div className="hidden md:block">Actions</div>
                            </div>

                            <div className="flex-1 overflow-y-auto min-h-0">
                                {loading ? (
                                    <div className="flex items-center justify-center h-32 text-gray-400">
                                        Loading chunks...
                                    </div>
                                ) : filteredChunks.length === 0 ? (
                                    <div className="flex items-center justify-center h-32 text-gray-400">
                                        {searchTerm ? "No chunks found matching your search" : "No chunks found"}
                                    </div>
                                ) : (
                                    filteredChunks.map(chunk => (
                                        <div
                                            key={chunk.id}
                                            className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_4fr_2fr_1fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors"
                                        >
                                            <div className="hidden md:block">
                                                <span className="font-medium text-sm">{chunk.id}</span>
                                            </div>

                                            <div className="hidden md:block">
                                                <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full ${
                                                    chunk.status === 'active'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {chunk.status}
                                                </span>
                                            </div>

                                            <div className="flex flex-col gap-0.5 min-w-0">
                                                <span className="font-medium text-xs text-gray-900 md:hidden">{chunk.id}</span>
                                                <p className="text-sm text-gray-600 line-clamp-2">{chunk.content}</p>
                                                <div className="flex flex-wrap gap-1 mt-1 md:hidden">
                                                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] uppercase rounded-full ${
                                                        chunk.status === 'active'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {chunk.status}
                                                    </span>
                                                    {chunk.topicTags?.slice(0, 1).map((tag, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-block px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                    {(chunk.topicTags?.length || 0) > 1 && (
                                                        <span className="text-[10px] text-gray-500">
                                                            +{(chunk.topicTags?.length || 0) - 1}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="hidden md:block">
                                                <div className="flex flex-wrap gap-1">
                                                    {chunk.topicTags?.slice(0, 2).map((tag, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-block px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                    {(chunk.topicTags?.length || 0) > 2 && (
                                                        <span className="text-[10px] text-gray-500">
                                                            +{(chunk.topicTags?.length || 0) - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center">
                                                <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full whitespace-nowrap ${
                                                    chunk.vectorIndexed
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {chunk.vectorIndexed ? 'Yes' : 'No'}
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-end gap-1 md:gap-2">
                                                <button
                                                    onClick={() => handleUpdateIndex(chunk.id)}
                                                    disabled={updatingChunks.has(chunk.id)}
                                                    className="p-1.5 rounded bg-green-100 hover:bg-green-200 text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Update vector index"
                                                >
                                                    <ArrowPathIcon className={`w-4 h-4 ${updatingChunks.has(chunk.id) ? 'animate-spin' : ''}`} />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingChunk(chunk);
                                                        setFormData({
                                                            id: chunk.id,
                                                            content: chunk.content,
                                                            sourceDocument: chunk.sourceDocument,
                                                            topicTags: chunk.topicTags || [],
                                                            dataTypeTags: chunk.dataTypeTags || [],
                                                            topicTagsInput: (chunk.topicTags || []).join(', '),
                                                            dataTypeTagsInput: (chunk.dataTypeTags || []).join(', '),
                                                        });
                                                        setShowForm(true);
                                                    }}
                                                    className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                                    title="Edit chunk"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Prompts Tab */}
                {activeTab === 'prompts' && (
                    <>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 shrink-0">
                            <button
                                className="btn btn-primary btn-sm !h-9 md:!h-10 text-xs md:text-sm"
                                onClick={() => setShowPromptForm(true)}
                            >
                                <PlusIcon className="w-4 h-4" />
                                Add Prompt
                            </button>
                        </div>

                        {/* Stats Row */}
                        <div className="w-full grid grid-cols-2 gap-2 shrink-0">
                            <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                                <div className="text-xl md:text-2xl text-primary font-bold">
                                    {prompts.length}
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Prompts</div>
                            </div>
                            <div className="w-full bg-white rounded-xl flex flex-col p-3 md:p-4 gap-1 md:gap-2 border">
                                <div className="text-xl md:text-2xl text-blue-700 font-bold">
                                    {prompts.reduce((acc, p) => acc + (p.variables?.length || 0), 0)}
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total Variables</div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center gap-6 relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search prompts..."
                                    className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    value={promptSearchTerm}
                                    onChange={(e) => setPromptSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Prompts List */}
                        <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                            <div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_3fr_2fr_auto] gap-2 md:gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                                <div>Name</div>
                                <div className="hidden md:block">Description</div>
                                <div className="hidden md:block">Variables</div>
                                <div>Actions</div>
                            </div>

                            <div className="flex-1 overflow-y-auto min-h-0">
                                {promptsLoading ? (
                                    <div className="flex items-center justify-center h-32 text-gray-400">
                                        Loading prompts...
                                    </div>
                                ) : filteredPrompts.length === 0 ? (
                                    <div className="flex items-center justify-center h-32 text-gray-400">
                                        {promptSearchTerm ? "No prompts found matching your search" : "No prompts found"}
                                    </div>
                                ) : (
                                    filteredPrompts.map(prompt => (
                                        <div
                                            key={prompt.id}
                                            className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_3fr_2fr_auto] gap-2 md:gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors"
                                        >
                                            <div className="flex flex-col gap-0.5 min-w-0">
                                                <span className="font-medium text-sm">{prompt.name}</span>
                                                <p className="text-xs text-gray-500 line-clamp-1 md:hidden">
                                                    {prompt.description}
                                                </p>
                                            </div>

                                            <div className="hidden md:block">
                                                <p className="text-sm text-gray-600 line-clamp-2">{prompt.description}</p>
                                            </div>

                                            <div className="hidden md:block">
                                                <div className="flex flex-wrap gap-1">
                                                    {prompt.variables?.slice(0, 3).map((variable, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-block px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-800 rounded font-mono"
                                                        >
                                                            {`{{${variable}}}`}
                                                        </span>
                                                    ))}
                                                    {(prompt.variables?.length || 0) > 3 && (
                                                        <span className="text-[10px] text-gray-500">
                                                            +{(prompt.variables?.length || 0) - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-end gap-1 md:gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingPrompt(prompt);
                                                        setPromptFormData({
                                                            name: prompt.name,
                                                            template: prompt.template,
                                                            description: prompt.description || '',
                                                            variablesInput: (prompt.variables || []).join(', '),
                                                        });
                                                        setShowPromptForm(true);
                                                    }}
                                                    className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                                    title="Edit prompt"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Chunk Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-heading">
                                    {editingChunk ? 'Edit Chunk' : 'Add New Chunk'}
                                </h3>
                                <button
                                    onClick={cancelEdit}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="px-4 sm:px-6 py-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Chunk ID</label>
                                        <input
                                            type="text"
                                            value={formData.id}
                                            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Source Document</label>
                                        <input
                                            type="text"
                                            value={formData.sourceDocument}
                                            onChange={(e) => setFormData({ ...formData, sourceDocument: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                                        <textarea
                                            value={formData.content}
                                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3 h-32 sm:h-24"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Topic Tags (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={formData.topicTagsInput}
                                            onChange={(e) => setFormData({ ...formData, topicTagsInput: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                            placeholder="e.g. nutrition, wellness, fitness"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Data Type Tags (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={formData.dataTypeTagsInput}
                                            onChange={(e) => setFormData({ ...formData, dataTypeTagsInput: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                            placeholder="e.g. research, survey, clinical"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={cancelEdit}
                                        className="bg-gray-100 hover:bg-gray-200 p-2 px-4 rounded-md text-sm transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-black text-white p-2 px-4 rounded-md text-sm flex items-center gap-2"
                                    >
                                        <CheckIcon className="w-4 h-4" />
                                        {editingChunk ? 'Update' : 'Create'} Chunk
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Prompt Form Modal */}
                {showPromptForm && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-heading">
                                    {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
                                </h3>
                                <button
                                    onClick={cancelPromptEdit}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handlePromptSubmit}>
                                <div className="px-4 sm:px-6 py-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                        <input
                                            type="text"
                                            value={promptFormData.name}
                                            onChange={(e) => setPromptFormData({ ...promptFormData, name: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                            required
                                            disabled={!!editingPrompt}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <input
                                            type="text"
                                            value={promptFormData.description}
                                            onChange={(e) => setPromptFormData({ ...promptFormData, description: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                            placeholder="Brief description of what this prompt does"
                                        />
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">Template</label>
                                            <div className="flex rounded-md overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowTemplatePreview(false)}
                                                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                                                        !showTemplatePreview
                                                            ? 'bg-primary text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowTemplatePreview(true)}
                                                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                                                        showTemplatePreview
                                                            ? 'bg-primary text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    Preview
                                                </button>
                                            </div>
                                        </div>
                                        {!showTemplatePreview ? (
                                            <textarea
                                                value={promptFormData.template}
                                                onChange={(e) => setPromptFormData({ ...promptFormData, template: e.target.value })}
                                                className="w-full bg-gray-100 rounded-md p-3 h-64 font-mono text-sm"
                                                required
                                                placeholder="Enter your prompt template. Use {{variableName}} for variables."
                                            />
                                        ) : (
                                            <div className="w-full bg-white border rounded-md p-4 h-64 overflow-y-auto prose prose-sm max-w-none">
                                                    <ReactMarkdown
                                                    components={{
                                                        p: ({ children }) => (
                                                            <p className="mb-4 last:mb-0">{children}</p>
                                                        ),
                                                        strong: ({ children }) => (
                                                            <strong className="font-semibold text-gray-900">{children}</strong>
                                                        ),
                                                        em: ({ children }) => (
                                                            <em className="italic">{children}</em>
                                                        ),
                                                        ul: ({ children }) => (
                                                            <ul className="list-disc ml-4 mb-4 space-y-1">{children}</ul>
                                                        ),
                                                        ol: ({ children }) => (
                                                            <ol className="list-decimal ml-4 mb-4 space-y-1">{children}</ol>
                                                        ),
                                                        li: ({ children }) => (
                                                            <li className="pl-2">{children}</li>
                                                        ),
                                                        h1: ({ children }) => (
                                                            <h1 className="text-lg font-bold mb-4">{children}</h1>
                                                        ),
                                                        h2: ({ children }) => (
                                                            <h2 className="text-base font-bold mb-4">{children}</h2>
                                                        ),
                                                        h3: ({ children }) => (
                                                            <h3 className="text-sm font-bold mb-1">{children}</h3>
                                                        ),
                                                        pre: ({ children }) => (
                                                            <pre className="bg-gray-800 text-gray-100 p-3 rounded-md my-2 overflow-x-auto text-sm">{children}</pre>
                                                        ),
                                                        code: ({ className, children }) => {
                                                            const isCodeBlock = className?.includes('language-');
                                                            return isCodeBlock ? (
                                                                <code className="text-sm">{children}</code>
                                                            ) : (
                                                                <code className="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
                                                            );
                                                        },
                                                    }}
                                                >
                                                    {promptFormData.template.replace(/^[\t ]+/gm, '')}
                                                </ReactMarkdown>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Variables (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={promptFormData.variablesInput}
                                            onChange={(e) => setPromptFormData({ ...promptFormData, variablesInput: e.target.value })}
                                            className="w-full bg-gray-100 rounded-md p-3"
                                            placeholder="e.g. userName, context, question"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            List the variable names used in your template (without the curly braces)
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={cancelPromptEdit}
                                        className="bg-gray-100 hover:bg-gray-200 p-2 px-4 rounded-md text-sm transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-black text-white p-2 px-4 rounded-md text-sm flex items-center gap-2"
                                    >
                                        <CheckIcon className="w-4 h-4" />
                                        {editingPrompt ? 'Update' : 'Create'} Prompt
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* CSV Upload Modal */}
                {showCSVUpload && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50">
                        <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-heading flex items-center gap-2">
                                    <DocumentTextIcon className="w-5 h-5" />
                                    Upload CSV File
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowCSVUpload(false);
                                        setCSVUploadResult(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="px-4 sm:px-6 py-4 space-y-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                                    <h4 className="font-semibold text-blue-900 mb-2 text-sm">CSV Format Requirements:</h4>
                                    <p className="text-xs sm:text-sm text-blue-800 mb-2">Your CSV file must have the following columns:</p>
                                    <ul className="list-disc list-inside text-xs sm:text-sm text-blue-800 space-y-1">
                                        <li><code className="bg-white px-1 rounded">chunk_id</code> - Unique identifier for the chunk</li>
                                        <li><code className="bg-white px-1 rounded">content</code> - The text content of the chunk</li>
                                        <li><code className="bg-white px-1 rounded">topic_tags</code> - Comma-separated topic tags</li>
                                        <li><code className="bg-white px-1 rounded">data_type_tags</code> - Comma-separated data type tags</li>
                                    </ul>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={handleCSVUpload}
                                        disabled={csvUploading}
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
                                    />
                                </div>

                                {csvUploading && (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                        <span className="ml-3 text-gray-700">Uploading CSV...</span>
                                    </div>
                                )}

                                {csvUploadResult && (
                                    <div className={`rounded-lg p-3 sm:p-4 ${
                                        csvUploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                                    }`}>
                                        <h4 className={`font-semibold mb-2 text-sm ${
                                            csvUploadResult.success ? 'text-green-900' : 'text-red-900'
                                        }`}>
                                            {csvUploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                                        </h4>
                                        <p className={`text-sm mb-2 ${
                                            csvUploadResult.success ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            {csvUploadResult.message}
                                        </p>

                                        {csvUploadResult.results && (
                                            <div className="space-y-2">
                                                <div className="text-sm text-gray-700">
                                                    <span className="font-medium">Total Records:</span> {csvUploadResult.results.total}
                                                </div>
                                                <div className="text-sm text-green-700">
                                                    <span className="font-medium">Successful:</span> {csvUploadResult.results.successful}
                                                </div>
                                                {csvUploadResult.results.failed > 0 && (
                                                    <>
                                                        <div className="text-sm text-red-700">
                                                            <span className="font-medium">Failed:</span> {csvUploadResult.results.failed}
                                                        </div>
                                                        {csvUploadResult.results.failedChunks.length > 0 && (
                                                            <div className="mt-2 max-h-32 overflow-y-auto">
                                                                <p className="text-sm font-medium text-red-900 mb-1">Failed Chunks:</p>
                                                                {csvUploadResult.results.failedChunks.map((chunk, idx) => (
                                                                    <div key={idx} className="text-xs text-red-700 ml-2">
                                                                        - {chunk.chunk_id}: {chunk.error}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setShowCSVUpload(false);
                                        setCSVUploadResult(null);
                                    }}
                                    className="bg-gray-100 hover:bg-gray-200 p-2 px-4 rounded-md text-sm transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default LLMManagementPage;
